import React, {
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
  useCallback,
  useMemo,
} from 'react';
import Sockette from 'sockette';
import {
  Grid,
  Paper,
  Toolbar,
  Stack,
  Typography,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  AlertTitle,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  Button,
} from '@mui/material';
import { Pause, PlayArrow, FileDownloadOutlined } from '@mui/icons-material';
import { useUser, useAuth } from '@/providers';
import { getSession } from '@/actions/session';
import { isUsableSession } from '@/services/auth';

import toNiceTimestamp from '../../../util/toNiceTimestamp';
import exportLogsToCsv from '../../../util/exportLogsToCsv';

const dedupAndSortMessages = (messages) => {
  const new_messages = messages.filter(function (a) {
    return !this[a.time] && (this[a.time] = true);
  }, Object.create(null));
  new_messages.sort((a, b) => b.time - a.time);
  return new_messages;
};

export default function ExecutionLogs({
  entity,
  entity_type,
  workers = undefined,
  enableWorkerFilter = false,
}) {
  const tableEl = useRef();
  const session = useAuth();
  const user = useUser();
  const [rows, setRows] = useState([]);
  const [distanceBottom, setDistanceBottom] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [atBottom, setAtBottom] = useState(true);

  const [logsLoading, setLogsLoading] = useState(false);
  const [initial_loading, setInitialLoading] = useState(true);
  const [tokens, setTokens] = useState({});
  const [selectedStream, setSelectedStream] = useState(null);
  const [showNoLogsAlert, setShowNoLogsAlert] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [bufferedLogsCount, setBufferedLogsCount] = useState(0);
  const isPausedRef = useRef(false);
  const isLoadingMoreRef = useRef(false);
  const pausedLogsBuffer = useRef([]);
  const ws = useRef(null);
  const heartbeat = useRef(null);

  // Initialize worker numbers mapping and tokens
  const workerNumbers = useRef({});
  const validWorkers = useMemo(
    () =>
      workers
        ? workers.filter((worker) => worker != null && worker.job_worker_uuid)
        : [],
    [workers],
  );
  const hasMultipleStreams = validWorkers.length > 0;
  const hasMultipleStreamsRef = useRef(hasMultipleStreams);

  // Initialize tokens and worker numbers when workers are provided
  useEffect(() => {
    hasMultipleStreamsRef.current = hasMultipleStreams;
    if (hasMultipleStreams) {
      const initialTokens = {};
      const workerNums = {};
      validWorkers.forEach((worker, index) => {
        if (worker?.job_worker_uuid) {
          initialTokens[worker.job_worker_uuid] = null;
          workerNums[worker.job_worker_uuid] = index + 1;
        }
      });
      setTokens(initialTokens);
      workerNumbers.current = workerNums;

      // Auto-select single worker if worker filter is enabled
      if (
        enableWorkerFilter &&
        validWorkers.length === 1 &&
        validWorkers[0]?.job_worker_uuid
      ) {
        setSelectedStream(validWorkers[0].job_worker_uuid);
      }
    } else {
      // Collections case: single stream
      setTokens({});
      workerNumbers.current = {};
      setSelectedStream(null);
    }
  }, [workers, enableWorkerFilter, hasMultipleStreams, validWorkers]);

  useEffect(() => {
    // Only create WebSocket when we have a usable session (token may be refreshed elsewhere)
    if (!session?.isLoggedIn || !isUsableSession(session)) {
      return;
    }

    let cancelled = false;

    const connect = (idToken) => {
      if (cancelled || !idToken) return;

      // Close existing connection if it exists
      if (ws.current) {
        ws.current.close();
        if (heartbeat.current) {
          clearInterval(heartbeat.current);
          heartbeat.current = null;
        }
      }

      ws.current = new Sockette(
        `${import.meta.env.VITE_WS_API_URL}?Authorization=${idToken}`,
        {
        timeout: 5e3,
        maxAttempts: 1,
        onopen: (e) => {
          ws.current.json({
            action: 'subscribe',
            data: {
              type: 'logs',
              entity: entity_type,
              id: entity.id,
              project_uuid: user.selected_project,
            },
          });
          ws.current.json({
            action: 'getlogs',
            data: {
              type: 'init',
              entity: entity_type,
              id: entity.id,
              project_uuid: user.selected_project,
            },
          });
          heartbeat.current = setInterval(
            () => {
              ws.current.json({
                action: 'heartbeat',
              });
            },
            9 * 60 * 1000,
          );
        },
        onmessage: (e) => {
          const { type, msg, time, stream } = JSON.parse(e.data);
          switch (type) {
            case 'next_log':
              // Store token per stream (works for both single and multiple streams)
              setTokens((prev) => ({ ...prev, [stream]: msg }));
              setLogsLoading(false);
              setInitialLoading(false);
              // Reset loading more flag when we get the next_log token
              isLoadingMoreRef.current = false;
              // If we get a next_log response but no rows yet, show no logs alert
              setRows((currentRows) => {
                if (currentRows.length === 0) {
                  setShowNoLogsAlert(true);
                }
                return currentRows;
              });
              break;
            case 'subscription':
              const newLogEntry = {
                msg,
                time,
                id: hasMultipleStreamsRef.current ? `${stream}:${time}` : time,
                stream,
                worker_num: workerNumbers.current[stream] || undefined,
              };

              // Always add logs from loadMore, even if paused
              // Only buffer real-time logs when paused
              if (isPausedRef.current && !isLoadingMoreRef.current) {
                // Buffer logs when paused (but not during loadMore)
                pausedLogsBuffer.current.push(newLogEntry);
                setBufferedLogsCount(pausedLogsBuffer.current.length);
              } else {
                // Add to rows when not paused, or when loading more
                setRows((prev) => {
                  const newRows = dedupAndSortMessages([...prev, newLogEntry]);
                  // Hide no logs alert when we receive the first log message
                  if (newRows.length > 0) {
                    setShowNoLogsAlert(false);
                  }
                  return newRows;
                });
              }
              break;
            case 'end':
              // this isn't needed for the web version and will prevent the infinite scroll from working
              break;
            default:
              console.log('invalid message type', type);
          }
        },
        onclose: (e) => {
          console.log('close', e);
          if (heartbeat.current) {
            clearInterval(heartbeat.current);
            heartbeat.current = null;
          }
        },
        onerror: (e) => {
          console.log('error', e);
          if (heartbeat.current) {
            clearInterval(heartbeat.current);
            heartbeat.current = null;
          }
        },
      },
    );
    };

    // Always use the latest token when opening the WebSocket (idToken is refreshed
    // periodically by Layout; this component must not use a stale token from context).
    getSession()
      .then((sessionData) => {
        if (cancelled || !isUsableSession(sessionData)) return;
        connect(sessionData.credentials.idToken);
      })
      .catch(() => {
        // Session fetch failed; avoid opening WS with stale/missing token
      });

    return () => {
      cancelled = true;
      if (ws.current) {
        ws.current.close();
        ws.current = null;
      }
      if (heartbeat.current) {
        clearInterval(heartbeat.current);
        heartbeat.current = null;
      }
    };
  }, [
    session?.isLoggedIn,
    session?.expires,
    session?.credentials?.idToken,
    user.selected_project,
    entity.id,
    entity_type,
  ]);

  const loadMore = useCallback(() => {
    setLogsLoading(true);
    isLoadingMoreRef.current = true;

    if (enableWorkerFilter && selectedStream) {
      // Single stream selected: query only that stream
      const token = tokens[selectedStream];
      if (token) {
        ws.current.json({
          action: 'getlogs',
          data: {
            type: 'next',
            entity: entity_type,
            id: entity.id,
            project_uuid: user.selected_project,
            stream: selectedStream,
            token: token,
          },
        });
      } else {
        setLogsLoading(false);
        setHasMore(false);
      }
    } else if (hasMultipleStreamsRef.current) {
      // Multiple streams: query all streams that have tokens
      let queryCount = 0;
      Object.entries(tokens).forEach(([stream, token]) => {
        if (token) {
          queryCount++;
          ws.current.json({
            action: 'getlogs',
            data: {
              type: 'next',
              entity: entity_type,
              id: entity.id,
              project_uuid: user.selected_project,
              stream: stream,
              token: token,
            },
          });
        }
      });
      if (queryCount === 0) {
        setLogsLoading(false);
        setHasMore(false);
      }
    } else {
      // Collections case: single stream (backward compatible)
      // Find the stream key from tokens (should be only one for Collections)
      const streamEntries = Object.entries(tokens);
      if (streamEntries.length > 0) {
        const [streamKey, token] = streamEntries[0];
        ws.current.json({
          action: 'getlogs',
          data: {
            type: 'next',
            entity: entity_type,
            id: entity.id,
            project_uuid: user.selected_project,
            stream: streamKey,
            token: token,
          },
        });
      } else {
        setLogsLoading(false);
        setHasMore(false);
        isLoadingMoreRef.current = false;
      }
    }

    // Check if we have no more data
    const allTokens = Object.values(tokens);
    if (allTokens.filter((token) => token).length === 0 && rows.length !== 0) {
      setHasMore(false);
      isLoadingMoreRef.current = false;
    }
  }, [
    rows,
    entity.id,
    user,
    tokens,
    selectedStream,
    entity_type,
    enableWorkerFilter,
  ]);

  const handleResume = useCallback(() => {
    // Merge buffered logs into rows
    if (pausedLogsBuffer.current.length > 0) {
      setRows((prev) => {
        const newRows = dedupAndSortMessages([
          ...prev,
          ...pausedLogsBuffer.current,
        ]);
        // Clear the buffer
        pausedLogsBuffer.current = [];
        setBufferedLogsCount(0);
        return newRows;
      });
    }
    setIsPaused(false);
    isPausedRef.current = false;
    setBufferedLogsCount(0);
  }, []);

  const handlePauseToggle = useCallback(() => {
    if (isPausedRef.current) {
      // Resume
      handleResume();
    } else {
      // Pause
      setIsPaused(true);
      isPausedRef.current = true;
    }
  }, [handleResume]);

  const handleExport = useCallback(() => {
    exportLogsToCsv(rows, {
      filename: `${entity.name}-execution-logs`,
      include_worker: hasMultipleStreams,
    });
  }, [rows, entity.name, hasMultipleStreams]);

  const scrollListener = useCallback(() => {
    let bottom = tableEl.current.scrollHeight - tableEl.current.clientHeight;
    // if you want to change distanceBottom every time new data is loaded
    // don't use the if statement
    if (!distanceBottom) {
      // calculate distanceBottom that works for you
      setDistanceBottom(Math.round(bottom * 0.2));
    }
    if (tableEl.current.scrollTop > bottom - distanceBottom) {
      setAtBottom(true);
      if (hasMore && !logsLoading) {
        loadMore();
      }
    } else {
      setAtBottom(false);
    }
  }, [hasMore, loadMore, logsLoading, distanceBottom]);

  useLayoutEffect(() => {
    if (tableEl?.current) {
      const tableRef = tableEl.current;
      tableRef.addEventListener('scroll', scrollListener);
      return () => {
        tableRef.removeEventListener('scroll', scrollListener);
      };
    }
  }, [scrollListener]);

  if (initial_loading) {
    return (
      <Grid size={{ xs: 12, md: 12, lg: 12 }}>
        <Alert severity='info' sx={{ '& .MuiAlert-message': { flexGrow: 1 } }}>
          <AlertTitle>Loading logs...</AlertTitle>
          <LinearProgress color='primary' />
        </Alert>
      </Grid>
    );
  }

  return (
    <>
      <Grid size={{ xs: 12, md: 12, lg: 12 }}>
        <Paper sx={{ display: 'flex', width: '100%' }}>
          <Paper sx={{ display: 'flex', width: '100%' }} elevation={1}>
            <Toolbar>
              <Stack
                spacing={2}
                direction='row'
                alignItems='center'
                sx={{ flexGrow: 1 }}
              >
                <Typography variant='h5'>
                  Execution Logs for <b>{entity.name}</b>
                </Typography>
                <IconButton
                  onClick={handlePauseToggle}
                  color='brand'
                  aria-label={isPaused ? 'Resume logs' : 'Pause logs'}
                  title={isPaused ? 'Resume logs' : 'Pause logs'}
                >
                  {isPaused ? <PlayArrow /> : <Pause />}
                </IconButton>
                <Button
                  onClick={handleExport}
                  disabled={rows.length === 0}
                  color='brand'
                  startIcon={<FileDownloadOutlined />}
                  aria-label='Export logs'
                >
                  Export
                </Button>
                {isPaused && bufferedLogsCount > 0 && (
                  <Typography variant='body2' color='text.secondary'>
                    Newer Logs Available
                  </Typography>
                )}
              </Stack>
            </Toolbar>
          </Paper>
        </Paper>
      </Grid>
      <Divider />
      {enableWorkerFilter && hasMultipleStreams && validWorkers.length > 1 ? (
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
            <Typography>Select to view logs for worker:</Typography>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id='worker-select-label'>Worker</InputLabel>
              <Select
                labelId='worker-select-label'
                value={selectedStream || ''}
                label='Worker'
                onChange={(e) => setSelectedStream(e.target.value || null)}
              >
                <MenuItem value=''>
                  <em>All</em>
                </MenuItem>
                {validWorkers.map((worker) => (
                  <MenuItem
                    key={worker.job_worker_uuid}
                    value={worker.job_worker_uuid}
                  >
                    Worker {workerNumbers.current[worker.job_worker_uuid]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>
      ) : null}
      <Grid size={{ xs: 12, md: 12, lg: 12 }}>
        <TableContainer
          style={{
            margin: 'auto',
            maxHeight: '800px',
          }}
          ref={tableEl}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {enableWorkerFilter && hasMultipleStreams && !selectedStream ? (
                  <TableCell>Worker</TableCell>
                ) : null}
                <TableCell>Message</TableCell>
                <TableCell>Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(enableWorkerFilter && selectedStream
                ? rows.filter((row) => row.stream === selectedStream)
                : rows
              ).map(({ msg, time, stream, worker_num }) => (
                <TableRow
                  key={
                    hasMultipleStreams ? `${stream}:${time}` : time + '' + msg
                  }
                >
                  {enableWorkerFilter &&
                  hasMultipleStreams &&
                  !selectedStream ? (
                    <TableCell>Worker {worker_num}</TableCell>
                  ) : null}
                  <TableCell sx={{ wordBreak: 'break-all' }}>{msg}</TableCell>
                  <TableCell>{toNiceTimestamp(time, true)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      {showNoLogsAlert && rows.length === 0 ? (
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <Alert severity='info'>
            <Typography>
              No logs currently available. Log messages are purged after 14
              days.
            </Typography>
          </Alert>
        </Grid>
      ) : undefined}
      {logsLoading ? (
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <Alert
            severity='info'
            sx={{ '& .MuiAlert-message': { flexGrow: 1 } }}
          >
            <AlertTitle>Loading...</AlertTitle>
            <LinearProgress color='primary' />
          </Alert>
        </Grid>
      ) : undefined}
      {enableWorkerFilter && hasMultipleStreams && !selectedStream ? (
        rows.length > 0 ? (
          <Grid size={{ xs: 12, md: 12, lg: 12 }}>
            <Alert severity='info'>
              <Typography>Select a worker to view older logs</Typography>
            </Alert>
          </Grid>
        ) : null
      ) : null}
      {!hasMore && atBottom && !(enableWorkerFilter && !selectedStream) ? (
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <Alert severity='info'>
            <Typography>There are no older results</Typography>
          </Alert>
        </Grid>
      ) : undefined}
    </>
  );
}
